'use client';


import {
  BoldPlugin,
  CodePlugin,
  ItalicPlugin,
  StrikethroughPlugin,
  UnderlinePlugin,
} from '@udecode/plate-basic-marks/react';
import {
  FontBackgroundColorPlugin,
  FontColorPlugin,
} from '@udecode/plate-font/react';
import { useEditorReadOnly } from '@udecode/plate/react';
import {
  BaselineIcon,
  BoldIcon,
  Code2Icon,
  ItalicIcon,
  PaintBucketIcon,
  StrikethroughIcon,
  UnderlineIcon,
  WandSparklesIcon,
} from 'lucide-react';

import { MoreDropdownMenu } from '@/components/plate-ui/more-dropdown-menu';
import { useLicense } from '@/hooks/useLicense';

import { AIToolbarButton } from './ai-toolbar-button';
import { AlignDropdownMenu } from './align-dropdown-menu';
import { ChapterExportToolbarButton } from './chapter-export-toolbar-button';
import { ChapterImportToolbarButton } from './chapter-import-toolbar-button';
import { ColorDropdownMenu } from './color-dropdown-menu';
import { CopilotToolbarButton } from './copilot-toolbar-button';
import { FontFamilyToolbarButton } from './font-family-toolbar-button';
import { FontSizeToolbarButton } from './font-size-toolbar-button';
import { RedoToolbarButton, UndoToolbarButton } from './history-toolbar-button';
import {
  BulletedIndentListToolbarButton,
  NumberedIndentListToolbarButton,
} from './indent-list-toolbar-button';
import { IndentTodoToolbarButton } from './indent-todo-toolbar-button';
import { IndentToolbarButton } from './indent-toolbar-button';
import { LineHeightDropdownMenu } from './line-height-dropdown-menu';
import { LinkToolbarButton } from './link-toolbar-button';
import { MarkToolbarButton } from './mark-toolbar-button';
import { OutdentToolbarButton } from './outdent-toolbar-button';
import { SaveToolbarButton } from './save-toolbar-button';
import { TableDropdownMenu } from './table-dropdown-menu';
import { ToggleToolbarButton } from './toggle-toolbar-button';
import { ToolbarGroup } from './toolbar';
import { TurnIntoDropdownMenu } from './turn-into-dropdown-menu';
// Typing effects button removed - settings managed in main settings page

export interface FixedToolbarButtonsProps {
  saveButton?: {
    onSave: () => void;
    saving?: boolean;
    disabled?: boolean;
  };
}

export function FixedToolbarButtons({ saveButton }: FixedToolbarButtonsProps) {
  const readOnly = useEditorReadOnly();
  const { hasAIFeatures } = useLicense();

  return (
    <div className="flex w-full">
      {!readOnly && (
        <>
          <ToolbarGroup>
            <UndoToolbarButton />
            <RedoToolbarButton />
            {saveButton && (
              <SaveToolbarButton
                onSave={saveButton.onSave}
                saving={saveButton.saving}
                disabled={saveButton.disabled}
              />
            )}
          </ToolbarGroup>



          <ToolbarGroup>
            <TurnIntoDropdownMenu />
            <FontFamilyToolbarButton />
            <FontSizeToolbarButton />
          </ToolbarGroup>

          {/* AI Commands Button - Only show for Pro users */}
          {hasAIFeatures && (
            <ToolbarGroup>
              <AIToolbarButton tooltip="AI Commands (⌘+J)">
                <WandSparklesIcon />
              </AIToolbarButton>
              <CopilotToolbarButton />
            </ToolbarGroup>
          )}

          <ToolbarGroup>
            <MarkToolbarButton nodeType={BoldPlugin.key} tooltip="Bold (⌘+B)">
              <BoldIcon />
            </MarkToolbarButton>

            <MarkToolbarButton
              nodeType={ItalicPlugin.key}
              tooltip="Italic (⌘+I)"
            >
              <ItalicIcon />
            </MarkToolbarButton>

            <MarkToolbarButton
              nodeType={UnderlinePlugin.key}
              tooltip="Underline (⌘+U)"
            >
              <UnderlineIcon />
            </MarkToolbarButton>

            <MarkToolbarButton
              nodeType={StrikethroughPlugin.key}
              tooltip="Strikethrough (⌘+⇧+M)"
            >
              <StrikethroughIcon />
            </MarkToolbarButton>

            <MarkToolbarButton nodeType={CodePlugin.key} tooltip="Code (⌘+E)">
              <Code2Icon />
            </MarkToolbarButton>

            <ColorDropdownMenu
              nodeType={FontColorPlugin.key}
              tooltip="Text color"
            >
              <BaselineIcon />
            </ColorDropdownMenu>

            <ColorDropdownMenu
              nodeType={FontBackgroundColorPlugin.key}
              tooltip="Background color"
            >
              <PaintBucketIcon />
            </ColorDropdownMenu>
          </ToolbarGroup>

          <ToolbarGroup>
            <AlignDropdownMenu />

            <NumberedIndentListToolbarButton />
            <BulletedIndentListToolbarButton />
            <IndentTodoToolbarButton />
            <ToggleToolbarButton />
          </ToolbarGroup>

          <ToolbarGroup>
            <LinkToolbarButton />
            <TableDropdownMenu />
          </ToolbarGroup>

          <ToolbarGroup>
            <LineHeightDropdownMenu />
            <OutdentToolbarButton />
            <IndentToolbarButton />
          </ToolbarGroup>

          <ToolbarGroup>
            <ChapterImportToolbarButton />
            <ChapterExportToolbarButton />
            <MoreDropdownMenu />
          </ToolbarGroup>
        </>
      )}

      <div className="grow" />
    </div>
  );
}
